import {
  Box,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Select,
  Portal,
  Spinner,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  useToast,
  useToken,
} from '@chakra-ui/react';
import {
  faCheckCircle,
  faEllipsisH,
  faExclamationTriangle,
  faEye,
  faEyeSlash,
  faTimes,
  faTrash,
  faUpload,
} from '@fortawesome/free-solid-svg-icons';
import { keys } from 'lodash';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMutation } from 'urql';
import { useTranslation } from 'react-i18next';
import {
  DeleteModelDocument,
  EventType,
  Model,
  ModelProcessingStatus,
  UpdateModelDocument,
} from '../../gql/graphql';
import { toLocaleDateStringWithHoursAndMinutes } from '../../utils/time-utils';
import ConfirmDeletionModal from '../common/ConfirmDeletionModal';
import { useViewer } from '../common/ForgeViewer';
import StyledKeyValuesAccordion from '../common/StyledKeyValuesProperties';
import SparkelIcon from '../common/icon/SparkelIcon';
import TriggerClassificationMenuItem from './TriggerClassificationMenuItem';
import { useProjectHistory } from './project-history/hooks/useProjectHistory';
import { useDiffing } from './project-history/hooks/useDiffing';
import { DiffBadge } from './project-history/DiffBadge';
import { useSelection } from 'src/services/viewer/selection';

type ModelSummary = Pick<
  Model,
  | 'id'
  | 'name'
  | 'filename'
  | 'status'
  | 'forgeUrn'
  | 'createdAt'
  | 'activeViewGuid'
  | 'availableViewGuids'
>;
type VersionedModelSummary = {
  models: ModelSummary[];
};

type BimModelItemProps = {
  model: VersionedModelSummary;
  handleUploadExistingVersion: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => Promise<void>;
  isUploadDisabled?: boolean;
  deleteModelVersion: () => Promise<void>;
  readOnly: boolean;
};

const ModelProcessingIcon = (
  state: ModelProcessingStatus
): React.ReactElement => {
  const [red500, green500] = useToken('colors', ['red.500', 'green.500']);
  switch (state) {
    case ModelProcessingStatus.Processing:
      return <Spinner color="brand.400" size="xs" />;
    case ModelProcessingStatus.Failed:
      return <SparkelIcon icon={faExclamationTriangle} color={red500} />;
    case ModelProcessingStatus.Complete:
      return <SparkelIcon icon={faCheckCircle} color={green500} />;
  }
};

// eslint-disable-next-line complexity
export default function BimModelItem({
  model,
  handleUploadExistingVersion: handleUploadModelVersion,
  isUploadDisabled,
  deleteModelVersion,
  readOnly,
}: BimModelItemProps) {
  const {
    loadedModels,
    visibilityManager: { hiddenModelUrns, showModel, hideModel },
  } = useViewer();

  const { selectedDbIds, setSelectedDbIds } = useSelection();

  const {
    isOpen: showDeleteModelModal,
    onOpen: onDeleteModelModalOpen,
    onClose: onDeleteModelModalClose,
  } = useDisclosure();

  const inputRef = useRef<HTMLInputElement>(null);
  const activeModel = model.models.sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();
    return dateA - dateB;
  })[model.models.length - 1];
  const activeButtonColor = useColorModeValue('gray.300', 'gray.500');
  const toast = useToast();

  const { t } = useTranslation('project');

  const uploadStatusMessage = (
    uploadStatus: ModelProcessingStatus | undefined
  ): string | undefined => {
    switch (uploadStatus) {
      case ModelProcessingStatus.Processing:
        return t('files.model.uploading');
      case ModelProcessingStatus.Failed:
        return t('files.model.failed');
    }
  };

  const [, deleteModel] = useMutation(DeleteModelDocument);
  const [, updateModel] = useMutation(UpdateModelDocument);

  const handleUpload = async () => {
    inputRef.current?.click();
  };

  const handleAbortUpload = async () => {
    const { error } = await deleteModel({ id: activeModel.id });
    if (error) {
      toast({
        status: 'error',
        title: t('toast.error.abort-model-upload'),
      });
      throw error;
    }
    toast({
      status: 'success',
      duration: 3000,
      title: t('toast.success.abort-model-upload'),
    });
  };

  const modelUrn = activeModel.forgeUrn;

  type AvailableView = { guid: string; name: string };
  const availableViews: AvailableView[] =
    (activeModel.availableViewGuids as AvailableView[]) ?? [];

  const currentActiveGuid = activeModel.activeViewGuid ?? null;

  const handleShowModel = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (modelUrn && modelUrn in loadedModels) {
        showModel(modelUrn);
      }
    },
    [modelUrn, loadedModels, showModel]
  );

  const handleHideModel = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (modelUrn) {
        hideModel(modelUrn);
      }
    },
    [modelUrn, hideModel]
  );

  const handleSelectModel = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (modelUrn && modelUrn in loadedModels) {
        showModel(modelUrn);

        setSelectedDbIds({});
        setSelectedDbIds({ [modelUrn]: [1] });
      }
    },
    [modelUrn, loadedModels, showModel, setSelectedDbIds]
  );

  const { isHistoryDrawerOpen, diff } = useProjectHistory();
  const { getTextColor, getBackgroundColor } = useDiffing();
  const [textColor, setTextColor] = useState<string | undefined>();
  const [backgroundColor, setBackgroundColor] = useState<string | undefined>();
  const [eventType, setEventType] = useState<EventType | undefined>();

  // eslint-disable-next-line complexity
  const getEventType = useCallback((): EventType | undefined => {
    if (!isHistoryDrawerOpen && !diff) return;
    // First we need to check if updated, otherwise we'll prematurely diagnose a created/deleted
    const results: Record<
      string,
      Record<'old' | 'new', unknown | undefined>
    > = {};
    const collectionKey = 'activeModels';
    for (const modelVersion of model.models) {
      const collectionDiff = diff?.[collectionKey];
      if (collectionDiff) {
        if (collectionDiff.old && collectionDiff.old !== '[]') {
          const oldResult = results[modelVersion.name];
          results[modelVersion.name] = oldResult
            ? { ...oldResult, old: collectionDiff.old }
            : { old: collectionDiff.old, new: undefined };
          continue;
        } else if (collectionDiff.new && collectionDiff.new !== '[]') {
          const oldResult = results[modelVersion.name];
          results[modelVersion.name] = oldResult
            ? { ...oldResult, new: collectionDiff.new }
            : { new: collectionDiff.new, old: undefined };
          continue;
        }
      }

      const currentModelKey = `activeModels.${modelVersion.id}`;
      const modelDiff = diff?.[currentModelKey];
      if (modelDiff) {
        if (modelDiff.old) {
          const oldResult = results[modelVersion.name];
          results[modelVersion.name] = oldResult
            ? { ...oldResult, old: modelDiff.old }
            : { old: modelDiff.old, new: undefined };
        } else if (modelDiff.new) {
          const oldResult = results[modelVersion.name];
          results[modelVersion.name] = oldResult
            ? { ...oldResult, new: modelDiff.new }
            : { new: modelDiff.new, old: undefined };
        }
      }
    }
    const modelDiff = results[activeModel.name];
    const oldVal = modelDiff?.old;
    const newVal = modelDiff?.new;
    if (!oldVal && !newVal) {
      return;
    } else if (oldVal && newVal) {
      return EventType.Modified;
    } else if (!oldVal && newVal) {
      return EventType.Created;
    } else if (oldVal && !newVal) {
      return EventType.Deleted;
    }
  }, [activeModel.name, model.models, diff, isHistoryDrawerOpen]);

  useEffect(() => {
    const eventType = getEventType();
    if (eventType) {
      const newTextColor = getTextColor(eventType);
      const newBackgroundColor = getBackgroundColor(eventType);
      setTextColor(newTextColor);
      setBackgroundColor(newBackgroundColor);
      setEventType(eventType);
    } else {
      setTextColor(undefined);
      setBackgroundColor(undefined);
      setEventType(undefined);
    }
  }, [getEventType, getTextColor, getBackgroundColor]);

  const isModelSelected = useMemo(() => {
    return (
      !!modelUrn &&
      modelUrn in loadedModels &&
      modelUrn in selectedDbIds &&
      selectedDbIds[modelUrn].length === 1 &&
      selectedDbIds[modelUrn][0] === 1
    );
  }, [selectedDbIds, modelUrn, loadedModels]);

  const hoverBackgroundColor = useColorModeValue('gray.50', 'gray.700');
  const checkedBackgroundColor = useColorModeValue('blue.50', 'blue.800');
  const checkedTextColor = useColorModeValue('blue.700', 'blue.200');

  return (
    <>
      <Flex
        id={activeModel.id}
        gap={1}
        role="group"
        backgroundColor={backgroundColor}
        color={textColor}
        aria-checked={isModelSelected}
        height={7}
        paddingY={0.5}
        paddingLeft={2}
        borderRadius={'lg'}
        onClick={handleSelectModel}
        cursor={modelUrn && modelUrn in loadedModels ? 'pointer' : 'default'}
        _hover={{
          backgroundColor: hoverBackgroundColor,
        }}
        _checked={{
          borderRadius: 'lg',
          bgColor: checkedBackgroundColor,
          textColor: checkedTextColor,
          fontWeight: 'bold',
        }}
      >
        <Flex overflow="hidden" justifyContent="flex-start" alignItems="center">
          <Tooltip
            placement="bottom-start"
            label={
              uploadStatusMessage(activeModel.status) ?? activeModel.filename
            }
            openDelay={400}
          >
            <Text
              textAlign="left"
              overflow="hidden"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
              fontSize="sm"
              {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                ...(hiddenModelUrns.includes(activeModel.forgeUrn!)
                  ? { color: 'gray.400' }
                  : {})
              }
            >
              {activeModel.filename}
            </Text>
          </Tooltip>
        </Flex>
        <HStack gap={0.5} role="group" position="absolute" right={4}>
          {!readOnly && !isHistoryDrawerOpen ? (
            <Menu closeOnSelect={false} isLazy placement="bottom-start">
              <MenuButton
                opacity={0}
                _groupHover={{
                  opacity: 1,
                  display: 'inline-block',
                }}
                _active={{ opacity: 1, background: activeButtonColor }}
                mr={0}
                isRound
                as={IconButton}
                colorScheme="gray"
                size={'xs'}
                maxWidth={16}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <SparkelIcon icon={faEllipsisH} />
              </MenuButton>

              <Portal>
                <MenuList maxW="xs" zIndex={99}>
                  <ModelInfo
                    name={activeModel.filename}
                    uploadedAt={activeModel.createdAt}
                  />
                  <MenuDivider />
                  {availableViews.length > 1 && (
                    <Box
                      width="100%"
                      px={3}
                      py={2}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Text fontSize="sm" mb={1} color="gray.500">
                        {t('files.model.set-active-view.title')}
                      </Text>
                      <Select
                        size="xs"
                        value={currentActiveGuid ?? ''}
                        borderRadius="md"
                        onChange={(e) => {
                          const guid = e.target.value;
                          if (!guid || guid === currentActiveGuid) return;
                          updateModel({
                            input: {
                              id: activeModel.id,
                              patch: { activeViewGuid: guid },
                            },
                          }).then(({ error }) => {
                            if (error) {
                              console.error(error);
                            }
                          });
                        }}
                      >
                        {availableViews.map((view) => (
                          <option key={view.guid} value={view.guid}>
                            {view.name}
                          </option>
                        ))}
                      </Select>
                    </Box>
                  )}
                  <TriggerClassificationMenuItem modelId={activeModel.id} />
                  <MenuDivider />
                  <MenuItem
                    isDisabled={isUploadDisabled}
                    icon={<SparkelIcon icon={faUpload} fixedWidth />}
                    onClick={async (e) => {
                      e.stopPropagation();
                      await handleUpload();
                    }}
                  >
                    {t('files.model.upload-new-version')}
                    <input
                      ref={inputRef}
                      className="d-none"
                      type="file"
                      onChange={handleUploadModelVersion}
                      style={{ display: 'none' }}
                    />
                  </MenuItem>
                  {activeModel.status === ModelProcessingStatus.Processing ? (
                    <MenuItem
                      icon={<SparkelIcon icon={faTimes} fixedWidth />}
                      onClick={handleAbortUpload}
                    >
                      {t('files.model.abort-upload')}
                    </MenuItem>
                  ) : null}
                  <MenuItem
                    icon={
                      <SparkelIcon
                        icon={faTrash}
                        color={'red.400'}
                        fixedWidth
                      />
                    }
                    textColor={'red.400'}
                    onClick={async (e) => {
                      e.stopPropagation();
                      onDeleteModelModalOpen();
                    }}
                  >
                    {t('files.model.delete')}
                  </MenuItem>
                </MenuList>
              </Portal>
            </Menu>
          ) : null}
          {activeModel.status === ModelProcessingStatus.Complete &&
          modelUrn &&
          modelUrn in loadedModels ? (
            !hiddenModelUrns.includes(modelUrn) ? (
              <Tooltip
                label={t('files.model.hide')}
                isDisabled={keys(loadedModels).length === 1}
              >
                <IconButton
                  opacity={0}
                  colorScheme="gray"
                  variant="ghost"
                  aria-label="hide model"
                  size="xs"
                  icon={<SparkelIcon icon={faEye} />}
                  isDisabled={keys(loadedModels).length === 1}
                  onClick={handleHideModel}
                  _groupHover={{ opacity: 1, display: 'inline-block' }}
                />
              </Tooltip>
            ) : (
              <Tooltip label={t('files.model.hide')}>
                <IconButton
                  colorScheme="gray"
                  variant="ghost"
                  textColor="gray.400"
                  size="xs"
                  aria-label="show model"
                  icon={<SparkelIcon color="gray.400" icon={faEyeSlash} />}
                  onClick={handleShowModel}
                />
              </Tooltip>
            )
          ) : (
            <Box marginRight={1}>{ModelProcessingIcon(activeModel.status)}</Box>
          )}
          {isHistoryDrawerOpen && diff && eventType ? (
            <DiffBadge eventType={eventType} />
          ) : null}
        </HStack>
      </Flex>
      <ConfirmDeletionModal
        onConfirm={deleteModelVersion}
        isOpen={showDeleteModelModal}
        onClose={onDeleteModelModalClose}
      >
        <Box>{t('files.model.delete-confirmation.title')}</Box>
        <Box>{t('files.model.delete-confirmation.message')}</Box>
      </ConfirmDeletionModal>
    </>
  );
}

type ModelInfoProps = {
  name: string;
  uploadedAt?: string | null;
};

function ModelInfo({ name, uploadedAt }: ModelInfoProps): React.ReactElement {
  const { t } = useTranslation('project');
  return (
    <>
      <Box mx={3}>
        <StyledKeyValuesAccordion
          keyValues={[
            {
              key: t('files.model.info.name'),
              stringValue: name,
            },
          ]}
        ></StyledKeyValuesAccordion>
      </Box>
      {uploadedAt && (
        <Box mx={3}>
          <StyledKeyValuesAccordion
            keyValues={[
              {
                key: t('files.model.info.uploaded-at'),
                stringValue: toLocaleDateStringWithHoursAndMinutes(uploadedAt),
              },
            ]}
          ></StyledKeyValuesAccordion>
        </Box>
      )}
    </>
  );
}
